/**
 * Notification Card Component
 * 
 * This component displays a single notification with a message and type.
 * Types include: success, error, warning, info.
 * Each type has its own visual styling.
 * 
 * The card can be closed manually by clicking the X button or automatically
 * after a timeout controlled by the parent as-notifications component.
 * 
 * The default timeout can be customized by setting the 'default-timeout'
 * attribute on the as-notifications parent component (in milliseconds),
 * or by providing a 'timeout' parameter in the notification event.
 */

import styleText from './as-notification-card.css?raw';
import { applyLocalizedText } from '../base.ts';
import { asApp } from '../../util/singletons.ts';
import { sendEv } from '../../data/events.ts';

const template = document.createElement('template');

template.innerHTML = /*html*/`
  <div class="notification-card">
    <div class="icon"></div>
    <div class="content-wrapper">
      <as-text class="content"></as-text>
      <div class="actions"></div>
    </div>
    <button class="close-button">
      <svg width="12" height="12" viewBox="0 0 24 24">
        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
      </svg>
    </button>
  </div>
`;

/**
 * Type-specific SVG icons
 */
const icons = {
  success: `<svg width="16" height="16" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>`,
  error: `<svg width="16" height="16" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>`,
  warning: `<svg width="16" height="16" viewBox="0 0 24 24"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>`,
  info: `<svg width="16" height="16" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>`
};

export interface NotificationAction {
  defaultText: string;
  dictKey?: string;
  vars?: any[];
  callback: () => void;
}

export class AsNotificationCard extends HTMLElement {
  static readonly tag = 'as-notification-card' as const;

  private _closeButton: HTMLButtonElement | null = null;
  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();
  
  static get observedAttributes() {
    return ['type', 'default-text', 'dict-key', 'vars'];
  }
  
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      AsNotificationCard._sheet
    ];
    this.shadowRoot?.appendChild(template.content.cloneNode(true));

    this._closeButton = this.shadowRoot?.querySelector('.close-button');
    this._closeButton?.addEventListener('click', this._onCloseClick);
  }
  
  connectedCallback() {
    this._updateIcon();
    this._updateContent();
  }
  
  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue === newValue) return;
    
    if (name === 'type') {
      this._updateIcon();
    } else if (name === 'default-text' || name === 'dict-key' || name === 'vars') {
      this._updateContent();
    }
  }
  
  disconnectedCallback() {
    this._closeButton?.removeEventListener('click', this._onCloseClick);
  }
  
  private _updateIcon() {
    const type = this.getAttribute('type') as keyof typeof icons || 'info';
    const iconContainer = this.shadowRoot?.querySelector('.icon');
    
    if (iconContainer) {
      iconContainer.innerHTML = icons[type] || icons.info;
    }
  }
  
  private _updateContent() {
    const defaultText = this.getAttribute('default-text') || '';
    const dictKey = this.getAttribute('dict-key');
    const vars = this.getAttribute('vars');
    const content = this.shadowRoot?.querySelector('.content') as HTMLElement | null;

    if (content) {
      // vars set before dict-key so they're in place when the key subscription registers.
      if (vars) content.setAttribute('vars', vars);
      else content.removeAttribute('vars');
      applyLocalizedText(content, dictKey, defaultText);
    }
  }

  setActions(actions: NotificationAction[]) {
    const actionsContainer = this.shadowRoot?.querySelector('.actions');
    if (!actionsContainer) return;
    actionsContainer.innerHTML = '';
    for (const action of actions) {
      const button = document.createElement('button');
      button.className = 'action-button';
      const text = document.createElement('as-text');
      if (action.vars) text.setAttribute('vars', JSON.stringify(action.vars));
      applyLocalizedText(text, action.dictKey, action.defaultText);
      button.appendChild(text);
      button.addEventListener('click', action.callback);
      actionsContainer.appendChild(button);
    }
  }

  // Doesn't remove the card — as-app owns that. This only reports the click.
  private _onCloseClick = (): void => {
    const id = this.getAttribute('id');
    
    if (id) {
      const app = asApp();
      
      // Falls back to this element when the app isn't found. sendEv's defaults
      // give it bubbles + composed, so it climbs out of the shadow root either way.
      sendEv(app ?? this, 'as-close-notification', { detail: { id } });
    }
  };
}

// Check if the element is already defined before registering it
if (!customElements.get(AsNotificationCard.tag)) {
  customElements.define(AsNotificationCard.tag, AsNotificationCard);
}
